import React, { useCallback, useContext, useState } from 'react';
import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Radio, Col, Form, Input, message, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { LOYALTY_ACTION } from '../../../../constants/define/translation';
import { appStyles, selectSelector } from '../../../../styles';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../../atoms';

import { CountryContext } from '../../../../repository/context/countryContext';
import { LoyaltyActionType } from '../../../../types/loyaltyAction';
import { LOYALTY_ACTION_FEATURES } from '../../../../constants/loyalty-action';
import {
  postCreateLoyaltyActionRepository,
  putUpdateLoyaltyActionRepository,
} from '../../../../repository/mytm/loyaltyAction/loyaltyActionRepository';
import { API_URLS } from '../../../../constants/urls/api-urls';

interface FormLoyaltyActionProps {
  record?: LoyaltyActionType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
}

export const FormLoyaltyAction = (props: FormLoyaltyActionProps) => {
  const { t } = useTranslation(LOYALTY_ACTION);
  const { record, onRefresh, onCloseModal, setDataRecord } = props;
  const [form] = Form.useForm();
  const [isCreateLoading, setCreateLoading] = useState(false);

  const countryContext = useContext(CountryContext);

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  const defaultValue = {
    name: record?.name,
    feature: record?.feature,
    annexActionId: record?.annexActionId,
    ctaLink: record?.ctaLink,
    countries: record?.countries || 'USA',
  };

  const onFinish = useCallback(
    (formValue: LoyaltyActionType) => {
      setCreateLoading(true);
      const payload = {
        ...formValue,
        annexActionId: Number(formValue.annexActionId),
        countries: formValue?.countries || 'USA',
      };

      if (record) {
        return putUpdateLoyaltyActionRepository(API_URLS.LOYALTY_ACTION_URL_WITH_ID(record.id), payload)
          .then(() => {
            onRefresh();
            onCloseModal();
            message.success(t('message.success.updateLoyalty'));
          })
          .catch(e => {
            setCreateLoading(true);
            message.error(e?.errorMessage);
          })
          .finally(() => {
            setCreateLoading(false);
            setDataRecord(null);
          });
      }

      return postCreateLoyaltyActionRepository(API_URLS.LOYALTY_ACTION_SAVE, payload)
        .then(() => {
          onRefresh();
          onCloseModal();
          setDataRecord(null);
          message.success(t('message.success.createLoyalty'));
        })
        .catch(e => {
          setCreateLoading(false);
          message.error(e?.errorMessage);
        });
    },
    [record],
  );

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultValue}
            onFinish={onFinish}
            name="form-loyalty-action"
          >
            <Row className="form-loyalty-action-container">
              <StyledFormLoyaltyAction span={24}>
                <div className="form-scroll">
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="annexActionId"
                        label={t('form.annexActionId')}
                        rules={[{ required: true, message: t('validate.annexActionIDBlank') }]}
                      >
                        <Input placeholder={t('form.annexActionId')} maxLength={255} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="name"
                        label={t('form.name')}
                        rules={[{ required: true, message: t('validate.nameBlank') }]}
                      >
                        <Input placeholder={t('form.name')} maxLength={255} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <SelectOption
                        modeForm
                        name="feature"
                        label={t('form.feature')}
                        placeholder={t('form.feature')}
                        textDisplay="text"
                        rules={[{ required: true, message: t('validate.featureBlank') }]}
                        values={LOYALTY_ACTION_FEATURES}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="ctaLink" label={t('form.ctaLink')}>
                        <Input placeholder={t('form.ctaLink')} maxLength={255} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="countries"
                        label={t('form.countries')}
                        rules={[{ required: true, message: t('validate.countryBlank') }]}
                      >
                        <Radio.Group options={countryOptions} disabled={!!record?.countries} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdateLoyaltyAction') : t('form.btnCreateLoyaltyAction')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormLoyaltyAction>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & .form-loyalty-action-container {
    padding: 1.5rem;

    .form-loyalty-action__description {
      resize: none;
    }
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;

const StyledFormLoyaltyAction = styled(Col)`
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }
`;
